import React, { useEffect, useState, useRef, useCallback } from 'react';
import { VbzModal, VbzButton } from '@smiles/voebiz-ui-kit';
import { ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import logoHeaderMobileVoebiz from '../../assets/voebiz-logo-header-mobile.svg';

interface IDefaultModal {
  id?: string;
  titleId?: string;
  btnPrimaryId?: string;
  btnSecondaryId?: string;
  messageId?: string;
  classNameBody?: string;
  className?: string;
  isOpen: boolean;
  translate?: string;
  valid?: boolean;
  textPrimary?: string;
  textSecondary?: string;
  iconTitle?: string;
  placeholder?: string;
  hideSecondaryButton?: boolean;
  hidePrimaryButton?: boolean;
  isLoadingPrimaryButton?: boolean;
  secondaryButtonFirst?: boolean;
  backdrop?: boolean;
  onClose?: () => void;
  onFinish?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  onSecondary?: () => void;
  toggle?: () => void;
}

const ModalDefaultMessage: React.FC<IDefaultModal> = ({
  id,
  titleId,
  btnPrimaryId,
  btnSecondaryId,
  messageId,
  classNameBody = 'modal-body-test',
  className = 'vbz-login-common',
  isOpen,
  translate = '',
  valid = true,
  hidePrimaryButton = false,
  isLoadingPrimaryButton = false,
  hideSecondaryButton = false,
  secondaryButtonFirst = false,
  backdrop = true,
  onClose,
  onFinish,
  onSecondary,
  toggle,
  children,
  iconTitle = '',
}) => {
  const { t, i18n } = useTranslation(translate);

  useEffect(() => {
    i18n.init();
  }, [i18n, translate]);

  const [fixedHeader, setFixedHeader] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const renderMessage = useCallback(() => {
    const data = t('message', { returnObjects: true });
    if (Array.isArray(data)) {
      return (
        <>
          {data.map((msg, index) => {
            const idx = index > 0 ? `_${index + 1}` : '';
            return (
              <p className="modal-description" id={`${messageId}${idx}`} key={msg}>
                {msg}
              </p>
            );
          })}
        </>
      );
    }

    return <p>{t('message')}</p>;
  }, [messageId, t]);

  const renderSecondaryButton = useCallback(
    (first: boolean) => {
      if (!hideSecondaryButton) {
        if (first && secondaryButtonFirst) {
          return (
            <p className="modal-link-text">
              {t('btn_secondary_prefix')}&nbsp;
              <VbzButton
                className="btn-modal-default-message"
                id={btnSecondaryId}
                text={t('btn_secondary')}
                color="hyperlink"
                onClick={onSecondary}
              />
              {t('btn_secondary_suffix')}
            </p>
          );
        } else if (!first && !secondaryButtonFirst) {
          return <VbzButton id={btnSecondaryId} text={t('btn_secondary')} color="hyperlink" onClick={onSecondary} />;
        }
      }

      return <></>;
    },
    [btnSecondaryId, secondaryButtonFirst, hideSecondaryButton, onSecondary],
  );

  const handleScroll = useCallback(() => {
    if (!modalContentRef.current) {
      return;
    }
    if (window.innerHeight > 750 || !isOpen) {
      return;
    }

    setFixedHeader(modalContentRef.current.getBoundingClientRect().y < 24);
  }, [modalContentRef, isOpen]);
  useEffect(() => {
    const modalContentDiv = document.querySelector(`.${classNameBody}`);

    if (modalContentDiv) {
      modalContentDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (modalContentDiv) {
        modalContentDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, [classNameBody, handleScroll, isOpen]);

  return (
    <VbzModal
      id={id}
      isOpen={isOpen}
      defaultHeader={!classNameBody}
      onClosed={onClose}
      type="right"
      className={className}
      backdrop={backdrop}
      toggle={toggle}
    >
      {classNameBody && (
        <div className="modal-header">
          <button type="button" className="vbz-btn-close" data-dismiss="modal" aria-label="Close" onClick={toggle}>
            <i className="material-icons" style={{ fontSize: '1rem' }}>
              close
            </i>
          </button>
          {fixedHeader && <span style={{ fontWeight: 'bold' }}>{t('title')}</span>}
        </div>
      )}

      <ModalBody className={classNameBody}>
        <div ref={modalContentRef}>
          <span className="default-modal-logo hide-desktop-modal">
            <img src={logoHeaderMobileVoebiz} alt="VoeBiz Logo" />
          </span>
          <h3 className="modal-default-message-title" id={titleId}>
            {t('title')} {iconTitle && <i className={iconTitle} />}
          </h3>

          {renderMessage()}
          {children}

          <div>
            {renderSecondaryButton(true)}
            {!hidePrimaryButton && (
              <VbzButton
                className="hide-mobile-modal"
                id={btnPrimaryId}
                text={t('btn_primary')}
                disabled={!valid}
                onClick={onFinish}
                isLoading={isLoadingPrimaryButton}
                style={hideSecondaryButton ? { marginTop: 64 } : undefined}
              />
            )}
            {renderSecondaryButton(false)}
          </div>
        </div>
      </ModalBody>
    </VbzModal>
  );
};

export default ModalDefaultMessage;
